import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  X,
  Calendar,
  Loader2,
  Mail,
  Phone,
  User as UserIcon,
  ArrowLeft,
  FileText,
  Shield,
  Upload,
  Image as ImageIcon,
} from "lucide-react";
import { usersService } from "../../../services/usersService";
import { alertError, alertSuccess } from "../../../helpers/alertError";
import { Layout } from "../../../layouts/DashboardLayout";

// Types
export interface User {
  id: string;
  name: string;
  first_name: string;
  last_name: string;
  interact_email: string;
  email: string;
  country: string;
  countryFlag: string;
  kycStatus: "pending" | "approved" | "rejected" | "incomplete" | "not_requested";
  riskLevel: "low" | "medium" | "high";
  registrationDate: string;
  avatar?: string;
  documents: {
    id: boolean;
    passport: boolean;
    address: boolean;
    selfie: boolean;
  };
  lastActivity: string;
  status?: "ACTIVE" | "INACTIVE";
  phonenumber?: string;
  is_verified?: "YES" | "NO" | "PENDING" | "REJECTED" | boolean | number;
  connexion_type?: "EMAIL" | "GOOGLE" | "FACEBOOK" | "PHONE";
  role?: "USER" | "ADMIN" | "MODERATOR";
  country_id?: number;
  created_at?: string;
  updated_at?: string;
  password?: string;
  is_deleted?: boolean;
  fcm_token?: string | null;
  kyc_status?: "ACTIVE" | "INACTIVE" | "NO_KYC";
  has_kyc?: boolean;
  birthdate?: string;
  expired_document_date?: string;
  transaction_stats?: {
    total_sent: number;
    total_sent_cad: number;
    total_sent_currency: string;
    transaction_count: number;
  };
}

export interface KYCDocument {
  id: string;
  user_id: string;
  document_type: string;
  passport_image: string | null;
  cni_permit_front_image: string | null;
  cni_permit_back_image: string | null;
  selfie_image: string | null;
  domicile_image: string | null;
  status: string;
  expired_document_date?: string;
  issued_document_date?: string;
  created_at: string;
  updated_at: string;
}

interface KYCFormData {
  documentType: "cni" | "passport" | "permit" | "";
  documentImage: File | null;
  frontImage: File | null;
  backImage: File | null;
  selfieImage: File | null;
  domicileImage: File | null;
  expiredDocumentDate: string;
  issuedDocumentDate: string;
}

const buildFormData = (user: User | null) => ({
  first_name: user?.first_name || "",
  last_name: user?.last_name || "",
  email: user?.email || "",
  phonenumber: user?.phonenumber || "",
  interact_email: user?.interact_email || "",
  birthdate: user?.birthdate?.split("T")[0] || "",
  expired_document_date: user?.expired_document_date?.split("T")[0] || "",
  status: user?.status || "ACTIVE",
  role: user?.role || "USER",
  connexion_type: user?.connexion_type || "EMAIL",
  is_verified: user?.is_verified || "NO",
  country: user?.country || "",
  countryFlag: user?.countryFlag || "",
  kycStatus: user?.kycStatus || "not_requested",
  riskLevel: user?.riskLevel || "low",
});

const buildKYCFormData = (kyc: KYCDocument | null, user?: User): KYCFormData => ({
  documentType: (kyc?.document_type as KYCFormData["documentType"]) || "",
  documentImage: null,
  frontImage: null,
  backImage: null,
  selfieImage: null,
  domicileImage: null,
  expiredDocumentDate: user?.expired_document_date?.split("T")[0] || "",
  issuedDocumentDate: kyc?.issued_document_date?.split("T")[0] || "",
});

// Composant réutilisable pour l'upload d'image
interface FileUploadProps {
  label: string;
  name: string;
  file: File | null;
  previewUrl?: string | null;
  onChange: (file: File | null) => void;
  required?: boolean;
  hint?: string;
}

const FileUpload: React.FC<FileUploadProps> = ({ label, name, file, previewUrl, onChange, required, hint }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [localPreview, setLocalPreview] = useState<string | null>(null);

  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setLocalPreview(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setLocalPreview(null);
    }
  }, [file]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null;
    onChange(selectedFile);
  };

  const handleRemove = () => {
    onChange(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const displayUrl = localPreview || previewUrl;

  return (
    <div>
      <label className="block mb-2 text-sm font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      <div className="flex flex-col gap-2">
        <input ref={inputRef} type="file" name={name} accept="image/*" onChange={handleFileChange} className="hidden" />

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
          >
            <Upload className="w-4 h-4" />
            {file ? "Changer le fichier" : "Choisir un fichier"}
          </button>

          {(file || previewUrl) && (
            <button
              type="button"
              onClick={handleRemove}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
            >
              <X className="w-4 h-4" />
              Retirer
            </button>
          )}
        </div>

        {file && (
          <p className="text-xs text-gray-500">
            Fichier : <span className="font-medium">{file.name}</span> ({(file.size / 1024).toFixed(1)} Ko)
          </p>
        )}

        {hint && !file && <p className="text-xs text-gray-500">{hint}</p>}

        {displayUrl ? (
          <div className="mt-2">
            <img src={displayUrl} alt={label} className="w-full h-40 object-cover rounded-lg border border-gray-200" />
            {previewUrl && !file && <p className="mt-1 text-xs text-gray-500 text-center">Image actuelle</p>}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center w-full h-40 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg">
            <ImageIcon className="w-8 h-8 text-gray-400" />
            <p className="mt-2 text-sm text-gray-500">Aucune image</p>
          </div>
        )}
      </div>
    </div>
  );
};

export const UpdateUserPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const userData = (location.state as { user?: User })?.user || null;

  const user = userData as User;

  console.log(user);

  const [formData, setFormData] = useState(() => buildFormData(user));
  const [kycFormData, setKycFormData] = useState<KYCFormData>(() => buildKYCFormData(null));
  const [kycData, setKycData] = useState<KYCDocument | null>(null);
  const [loading, setLoading] = useState(false);
  const [kycLoading, setKycLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<"user" | "kyc">("user");
  const [kycDataLoaded, setKycDataLoaded] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData(buildFormData(user));
      fetchKYCData();
    }
  }, [user?.id]);

  const fetchKYCData = async () => {
    if (!user) return;

    setKycLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alertError("Votre session est expirée. Veuillez vous reconnecter");
        return;
      }

      const response = await usersService.getUserWithKYC(user.id, token);
      console.log("kyc", response.data.kyc);
      if (response.data && response.data.kyc) {
        setKycData(response.data.kyc);
        setKycFormData((prev) => {
          const built = buildKYCFormData(response.data.kyc, user);
          return {
            ...built,
            documentImage: prev.documentImage,
            frontImage: prev.frontImage,
            backImage: prev.backImage,
            selfieImage: prev.selfieImage,
            domicileImage: prev.domicileImage,
          };
        });
        setKycDataLoaded(true);
      }
    } catch (error) {
      console.error("Error fetching KYC data:", error);
      alertError("Erreur lors du chargement des données KYC");
    } finally {
      setKycLoading(false);
    }
  };

  if (!user) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alertError("Votre session est expirée. Veuillez vous reconnecter");
        return;
      }

      const now = new Date().toISOString();

      const data = await usersService.updateUser(
        user.id,
        {
          ...(formData as any),
          created_at: user.created_at || now,
          updated_at: now,
        },
        token
      );

      if (!data?.data || (data.statusCode && data.statusCode !== 200)) {
        alertError(data?.message || "Erreur lors de la mise à jour de l'utilisateur");
        return;
      }

      alertSuccess("Utilisateur mis à jour avec succès");
    } catch (error) {
      console.error("Erreur lors de la mise à jour :", error);
      alertError("Erreur lors de la mise à jour de l'utilisateur");
    } finally {
      setLoading(false);
    }
  };

  const handleKYCSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validations
    if (!kycFormData.documentType) {
      alertError("Veuillez sélectionner un type de document");
      return;
    }

    if (kycFormData.documentType === "passport" && !kycFormData.documentImage && !kycData?.passport_image) {
      alertError("L'image du passeport est requise");
      return;
    }

    if (
      (kycFormData.documentType === "cni" || kycFormData.documentType === "permit") &&
      (!kycFormData.frontImage || !kycFormData.backImage || !kycFormData.selfieImage) &&
      (!kycData?.cni_permit_front_image || !kycData?.cni_permit_back_image || !kycData?.selfie_image)
    ) {
      alertError("Les images recto, verso et le selfie sont requis pour ce type de document");
      return;
    }

    // Debug
    console.log("=== AVANT ENVOI KYC ===", {
      documentType: kycFormData.documentType,
      frontImage: kycFormData.frontImage?.name,
      backImage: kycFormData.backImage?.name,
      selfieImage: kycFormData.selfieImage?.name,
      documentImage: kycFormData.documentImage?.name,
      domicileImage: kycFormData.domicileImage?.name,
    });

    setKycLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alertError("Votre session est expirée. Veuillez vous reconnecter");
        return;
      }

      const formPayload = new FormData();
      formPayload.append("documentType", kycFormData.documentType);
      formPayload.append("userId", user.id);

      if (kycFormData.documentImage) {
        formPayload.append("documentImage", kycFormData.documentImage);
      }
      if (kycFormData.frontImage) {
        formPayload.append("frontImage", kycFormData.frontImage);
      }
      if (kycFormData.backImage) {
        formPayload.append("backImage", kycFormData.backImage);
      }
      if (kycFormData.selfieImage) {
        formPayload.append("selfieImage", kycFormData.selfieImage);
      }
      if (kycFormData.domicileImage) {
        formPayload.append("domicileImage", kycFormData.domicileImage);
      }

      if (kycFormData.expiredDocumentDate) {
        formPayload.append("expiredDocumentDate", kycFormData.expiredDocumentDate);
        formPayload.append("expired_document_date", kycFormData.expiredDocumentDate);
      }
      if (kycFormData.issuedDocumentDate) {
        formPayload.append("issuedDocumentDate", kycFormData.issuedDocumentDate);
        formPayload.append("issued_document_date", kycFormData.issuedDocumentDate);
      }

      // Debug contenu FormData
      for (const [key, value] of formPayload.entries()) {
        console.log(`📤 ${key}:`, value instanceof File ? `File(${value.name})` : value);
      }

      await usersService.submitUserKYC(user.id, formPayload, token);

      alertSuccess("KYC mis à jour avec succès");
      await fetchKYCData();
    } catch (error) {
      console.error("Erreur lors de la mise à jour du KYC :", error);
      alertError("Erreur lors de la mise à jour du KYC");
    } finally {
      setKycLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleKYCFieldChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setKycFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (field: keyof KYCFormData) => (file: File | null) => {
    setKycFormData((prev) => ({ ...prev, [field]: file }));
  };

  const isPassport = kycFormData.documentType === "passport";
  const isCniOrPermit = kycFormData.documentType === "cni" || kycFormData.documentType === "permit";

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* En-tête */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
                disabled={loading || kycLoading}
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <h1 className="text-2xl font-bold text-gray-800">Modifier l'utilisateur</h1>
            </div>
            <button
              onClick={() => navigate(-1)}
              className="text-gray-400 hover:text-gray-600"
              disabled={loading || kycLoading}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Onglets */}
          <div className="flex gap-2 mb-6 border-b border-gray-200">
            <button
              onClick={() => setActiveTab("user")}
              className={`flex items-center gap-2 px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                activeTab === "user"
                  ? "border-indigo-600 text-indigo-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <UserIcon className="w-4 h-4" />
              Informations utilisateur
            </button>
            <button
              onClick={() => setActiveTab("kyc")}
              className={`flex items-center gap-2 px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                activeTab === "kyc"
                  ? "border-indigo-600 text-indigo-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <FileText className="w-4 h-4" />
              Documents KYC
            </button>
          </div>

          {/* Carte du formulaire */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            {activeTab === "user" ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Informations personnelles */}
                <div>
                  <h2 className="text-lg font-semibold text-gray-700 mb-4">Informations personnelles</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        <div className="flex items-center gap-2">
                          <UserIcon className="w-4 h-4" /> Prénom
                        </div>
                      </label>
                      <input
                        type="text"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        <div className="flex items-center gap-2">
                          <UserIcon className="w-4 h-4" /> Nom
                        </div>
                      </label>
                      <input
                        type="text"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Contact */}
                <div>
                  <h2 className="text-lg font-semibold text-gray-700 mb-4">Contact</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4" /> Email
                        </div>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4" /> Téléphone
                        </div>
                      </label>
                      <input
                        type="tel"
                        name="phonenumber"
                        value={formData.phonenumber}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4" /> Email Interac
                      </div>
                    </label>
                    <input
                      type="email"
                      name="interact_email"
                      value={formData.interact_email}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                {/* Dates */}
                <div>
                  <h2 className="text-lg font-semibold text-gray-700 mb-4">Dates</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" /> Date de naissance
                        </div>
                      </label>
                      <input
                        type="date"
                        name="birthdate"
                        value={formData.birthdate}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" /> Date d'expiration
                        </div>
                      </label>
                      <input
                        type="date"
                        name="expired_document_date"
                        value={formData.expired_document_date}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Statuts */}
                <div>
                  <h2 className="text-lg font-semibold text-gray-700 mb-4">Statuts & Rôles</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">Statut</label>
                      <select
                        name="status"
                        value={formData.status}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="ACTIVE">Actif</option>
                        <option value="INACTIVE">Inactif</option>
                      </select>
                    </div>
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">Rôle</label>
                      <select
                        name="role"
                        value={formData.role}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="USER">Utilisateur</option>
                        <option value="ADMIN">Administrateur</option>
                        <option value="MODERATOR">Modérateur</option>
                      </select>
                    </div>
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">Type de connexion</label>
                      <select
                        name="connexion_type"
                        value={formData.connexion_type}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="EMAIL">Email</option>
                        <option value="GOOGLE">Google</option>
                        <option value="FACEBOOK">Facebook</option>
                        <option value="PHONE">Téléphone</option>
                      </select>
                    </div>
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">Vérifié</label>
                      <select
                        name="is_verified"
                        value={String(formData.is_verified)}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="NO">Non</option>
                        <option value="PENDING">En attente</option>
                        <option value="REJECTED">Rejeté</option>
                        <option value="YES">Oui (approuvé)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">KYC Status</label>
                      <select
                        name="kycStatus"
                        value={formData.kycStatus}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="not_requested">Non demandé</option>
                        <option value="pending">En attente</option>
                        <option value="approved">Approuvé</option>
                        <option value="rejected">Rejeté</option>
                        <option value="incomplete">Incomplet</option>
                      </select>
                    </div>
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">Niveau de risque</label>
                      <select
                        name="riskLevel"
                        value={formData.riskLevel}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="low">Faible</option>
                        <option value="medium">Moyen</option>
                        <option value="high">Élevé</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    disabled={loading}
                    className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
                  >
                    {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                    Enregistrer
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleKYCSubmit} className="space-y-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    Informations KYC
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        Type de document <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="documentType"
                        value={kycFormData.documentType}
                        onChange={handleKYCFieldChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="">Sélectionner...</option>
                        <option value="cni">CNI</option>
                        <option value="passport">Passeport</option>
                        <option value="permit">Permis de conduire</option>
                      </select>
                    </div>

                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        Date d'émission du document
                      </label>
                      <input
                        type="date"
                        name="issuedDocumentDate"
                        value={kycFormData.issuedDocumentDate}
                        onChange={handleKYCFieldChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>

                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        Date d'expiration du document
                      </label>
                      <input
                        type="date"
                        name="expiredDocumentDate"
                        value={kycFormData.expiredDocumentDate}
                        onChange={handleKYCFieldChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Upload d'images */}
                <div>
                  <h2 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                    <Upload className="w-5 h-5" />
                    Documents à uploader
                  </h2>

                  {!kycFormData.documentType && (
                    <div className="p-4 mb-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-800">
                        Veuillez d'abord sélectionner un type de document pour afficher les champs d'upload
                        correspondants.
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {isPassport && (
                      <FileUpload
                        label="Image du passeport"
                        name="documentImage"
                        file={kycFormData.documentImage}
                        previewUrl={kycData?.passport_image}
                        onChange={handleFileChange("documentImage")}
                        required
                        hint="Format image (JPG, PNG). Taille maximale : 5 Mo."
                      />
                    )}

                    {isCniOrPermit && (
                      <>
                        <FileUpload
                          label="Recto CNI/Permis"
                          name="frontImage"
                          file={kycFormData.frontImage}
                          previewUrl={kycData?.cni_permit_front_image}
                          onChange={handleFileChange("frontImage")}
                          required
                        />
                        <FileUpload
                          label="Verso CNI/Permis"
                          name="backImage"
                          file={kycFormData.backImage}
                          previewUrl={kycData?.cni_permit_back_image}
                          onChange={handleFileChange("backImage")}
                          required
                        />
                      </>
                    )}

                    <FileUpload
                      label="Selfie"
                      name="selfieImage"
                      file={kycFormData.selfieImage}
                      previewUrl={kycData?.selfie_image}
                      onChange={handleFileChange("selfieImage")}
                      required={isCniOrPermit}
                      hint="Selfie de vérification d'identité."
                    />

                    <FileUpload
                      label="Justificatif de domicile"
                      name="domicileImage"
                      file={kycFormData.domicileImage}
                      previewUrl={kycData?.domicile_image}
                      onChange={handleFileChange("domicileImage")}
                      hint="Facture, quittance, etc."
                    />
                  </div>
                </div>

                {kycData && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium text-gray-800 mb-2">Informations KYC actuelles</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Statut :</span>
                        <p className="text-gray-800">{kycData.status}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Créé le :</span>
                        <p className="text-gray-800">{new Date(kycData.created_at).toLocaleDateString("fr-FR")}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Mis à jour le :</span>
                        <p className="text-gray-800">{new Date(kycData.updated_at).toLocaleDateString("fr-FR")}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex gap-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    disabled={kycLoading}
                    className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={kycLoading}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
                  >
                    {kycLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                    Enregistrer le KYC
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};
